import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContactsTierSelectManager } from '@ctct/application-layer';
import type { BillingStrategyQuery } from '@ctct/domain-layer';
import type { ContactsTierSelectHooks, ContactsTierSelectPageStore } from '@ctct/state-client-layer';
import { useContactsCount, usePlanGroupFacade } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { useIsCoreMutationLockedAccountState } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<ContactsTierSelectManager, 'contactsTierSelectInit'>;
  flags?: Parameters<ContactsTierSelectManager['contactsTierSelectInit']>[0]['flags'];
  pageStore: ContactsTierSelectPageStore;
  hooks: ContactsTierSelectHooks;
  billingStrategy: BillingStrategyQuery;
}

export const useContactsTierSelectWidget = ({
  initializer,
  pageStore,
  hooks,
  flags: partialFlags = {},
  billingStrategy,
}: Props) => {
  const { t } = useTranslation();

  const planGroupQuery = usePlanGroupFacade();
  const contactsCountQuery = useContactsCount();

  const { isLocked: isCoreMutationLockedAccount, isPending: isCoreMutationLockPending } =
    useIsCoreMutationLockedAccountState({ billingStrategy });
  const isConfirmedCoreMutationLockedAccount = Boolean(isCoreMutationLockedAccount && !isCoreMutationLockPending);
  const { skipInitializationFromUrl, isMWebVariantEnabled, isSmsBundle } = {
    skipInitializationFromUrl: false,
    isMWebVariantEnabled: false,
    isSmsBundle: false,
    ...partialFlags,
  };

  const { lifecycle, ...componentProps } = useMemo(() => {
    const returnValue = initializer.contactsTierSelectInit({
      pageStore,
      queries: [planGroupQuery, contactsCountQuery],
      flags: {
        skipInitializationFromUrl,
        isMWebVariantEnabled,
        isSmsBundle,
        isCoreMutationLockedAccount: isConfirmedCoreMutationLockedAccount,
        isCoreMutationLockPending: Boolean(isCoreMutationLockPending),
      },
      t,
    });

    return {
      ...returnValue,
      hooks,
      isSingleBillChildAccount: Boolean(isCoreMutationLockedAccount),
    };
  }, [
    initializer,
    pageStore,
    planGroupQuery,
    contactsCountQuery,
    skipInitializationFromUrl,
    isMWebVariantEnabled,
    isSmsBundle,
    t,
    hooks,
    isCoreMutationLockedAccount,
    isCoreMutationLockPending,
    isConfirmedCoreMutationLockedAccount,
  ]);

  useWidgetLifecycle(componentProps.status, lifecycle, isCoreMutationLockPending);

  return componentProps;
};

export type ContactsTierSelectWidgetProps = ReturnType<typeof useContactsTierSelectWidget>;
